@let profile = profileStateService.select('profile') | async;
@let userDetails = authStateService.select('userDetails') | async;
@let user = $safeNavigationMigration(userDetails?.user);
@let group = $safeNavigationMigration(userDetails?.organization);

<nav
    class="header-class border-border bg-surface text-text-primary z-[999] w-full border-b">
    <div class="mx-auto flex flex-wrap items-center justify-between px-4 py-3">
        <div class="flex items-center gap-3">
            <button
                type="button"
                (click)="toggleSidebar()"
                class="text-text-primary hover:bg-surface-muted flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg transition-colors">
                <i class="pi pi-bars text-xl"></i>
            </button>

            <a
                class="flex items-center md:hidden"
                [routerLink]="['/dashboard/home']">
                <img
                    src="assets/logo/hairstyleclub-icon.png"
                    class="h-7 w-7 cursor-pointer object-contain"
                    alt="Hair Style Club" />
            </a>
        </div>

        <div class="flex items-center gap-3 md:gap-4">
            <!-- Book Appointment Action (Prominent Button) -->
            <a
                routerLink="/dashboard/appointments/book"
                class="bg-button-1 hidden cursor-pointer items-center gap-2 rounded-full px-5 py-2 text-sm font-bold text-white shadow-xs transition-transform hover:opacity-95 active:scale-95 sm:inline-flex">
                <i class="pi pi-plus text-sm"></i>
                <span>{{
                    'vendorDashboard.homePage.quickActions.bookAppointment'
                        | transloco
                }}</span>
            </a>

            <div class="flex items-center gap-2">
                <div class="org-dropdown-wrapper relative hidden items-center">
                    <!-- Main Toggle Button -->
                    <div
                        (click)="toggleOrgDropdown()"
                        class="group border-border bg-surface flex cursor-pointer items-center gap-2 rounded-full border px-4 py-1.5 transition-all duration-300 active:scale-95">
                        <div
                            class="text-primary flex transition-colors duration-300">
                            <i
                                class="pi pi-building"
                                style="font-size: 1.1rem"></i>
                        </div>

                        <div class="flex flex-col">
                            <span
                                class="text-text-secondary text-[9px] font-semibold tracking-wider uppercase">
                                {{ profile?.firstName }} {{ profile?.lastName }}
                            </span>
                            <div
                                class="relative -mt-0.5 flex items-center gap-1.5">
                                <span
                                    class="text-text-primary text-xs font-bold">
                                    {{
                                        $safeNavigationMigration(
                                            orgService.organization()?.name
                                        ) | textSlice: 25
                                    }}
                                </span>
                                <i
                                    class="pi pi-chevron-down text-text-secondary text-[9px] transition-transform duration-300"
                                    [class.rotate-180]="
                                        orgService.isOrgDropdownVisible()
                                    "
                                    #orgChevron>
                                </i>
                            </div>
                        </div>
                    </div>

                    <!-- Dropdown Menu -->
                    @let groupData = userGroupStateService.selectAll() | async;
                    @let hasVendorGroup =
                        userGroupStateService.select('hasVendorGroup') | async;

                    @if (orgService.isOrgDropdownVisible()) {
                        <app-group-list-items
                            #orgDropdown
                            class="animate-in fade-in zoom-in-95 border-border bg-surface absolute top-12 -left-10 z-50 w-72 origin-top rounded-2xl border p-2 shadow-xl ring-1 ring-black/5 duration-200"
                            [groupData]="groupData ?? null"
                            [hasVendorGroup]="hasVendorGroup ?? false"
                            [selectedGroupId]="
                                orgService.selectedGroupId() ?? ''
                            "
                            [isLoading]="orgService.isLoading()"
                            [groupId]="
                                this.authStateService.getOrganizationId() ?? ''
                            "
                            (onNavigate)="onNavigate($event)"
                            (onSwitchGroup)="onSwitchGroup($event)" />
                    }
                </div>

                <!-- Quick Settings -->
                <app-header-quick-settings></app-header-quick-settings>
            </div>

            <div class="hidden md:block">
                <a
                    [routerLink]="['/dashboard/notifications']"
                    class="border-border bg-surface text-text-primary hover:bg-surface-muted flex h-10 w-10 items-center justify-center rounded-full border transition-colors">
                    <i class="pi pi-bell text-base"></i>
                </a>
            </div>

            @let profilePhoto =
                profileStateService.select('profilePhotoUrl') | async;
            <div (click)="onViewDropDown()">
                <user-login-status
                    [user]="
                        $safeNavigationMigration(user?.firstName) +
                        ' ' +
                        $safeNavigationMigration(user?.lastName)
                    "
                    [profilePhoto]="profilePhoto ?? ''"
                    [verifiedAt]="$safeNavigationMigration(user?.verifiedAt)"
                    [userFirstName]="user?.firstName ?? ''"
                    [userRole]="
                        groupType() === 'client'
                            ? ''
                            : $safeNavigationMigration(
                                  userDetails?.role?.name
                              ) || 'Employee'
                    " />
            </div>
        </div>

        @if (isVisible()) {
            <div
                #dropdown
                class="border-border bg-surface text-text-primary absolute top-16 right-4 z-[999] flex w-72 flex-col rounded-xl border p-1 shadow-lg lg:top-16"
                id="profileDropDown">
                <span class="text-text-primary px-3 py-2">
                    <span class="block text-lg font-bold">
                        {{ profile?.firstName }}
                        {{ profile?.lastName }}
                    </span>
                    <span
                        class="text-primary -mt-0.5 block text-xs font-semibold">
                        <i
                            style="font-size: 0.7rem"
                            class="pi pi-building mr-1"></i>
                        {{
                            $safeNavigationMigration(
                                orgService.organization()?.name
                            ) | textSlice: 40
                        }}
                    </span>
                </span>

                <hr class="opacity-30 dark:border-gray-400" />

                <app-navigate-button
                    [label]="'vendorDashboard.header.profile' | transloco"
                    [path]="'/profile'"
                    [icon]="'pi pi-user'"
                    [verifiedAt]="$safeNavigationMigration(user?.verifiedAt)"
                    [isBadge]="true"
                    (navigate)="onNavigate($event)" />

                @if (groupType() === 'vendor' && !isOrdinaryStaff()) {
                    <div
                        class="group dark-hover-bg flex cursor-pointer items-center justify-between gap-2 px-3 py-2 leading-relaxed transition-all hover:bg-slate-200"
                        (click)="onUpgradeSubscription()">
                        <div class="secondary-text flex items-center gap-2">
                            <i
                                class="pi pi-briefcase secondary-text font-medium text-black"></i>
                            <p fontSpanish>
                                {{
                                    'vendorDashboard.header.subscription'
                                        | transloco
                                }}
                            </p>
                        </div>

                        <span class="badge-green primary-text-2 capitalize">
                            {{ group?.planName }}
                        </span>
                    </div>
                }

                <app-navigate-button
                    [label]="
                        'vendorDashboard.sidebarVendor.items.settings'
                            | transloco
                    "
                    [path]="'/dashboard/settings'"
                    [icon]="'pi pi-cog'"
                    (navigate)="onNavigate($event)" />

                <hr class="opacity-30 dark:border-gray-400" />

                <hr class="opacity-30 dark:border-gray-400" />

                <button
                    (click)="logout()"
                    class="secondary-text dark-hover-bg flex cursor-pointer items-center justify-start gap-2 px-3 py-2 leading-relaxed text-red-800 transition-all">
                    <span class="pi pi-sign-out font-medium"></span>
                    <span fontSpanish>{{
                        'vendorDashboard.header.logout' | transloco
                    }}</span>
                </button>
            </div>
        }
    </div>
</nav>
